import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useThemeMode } from '../contexts/ThemeContext';
import { useThemeColors } from '../hooks/useThemeColors';
import {
  fetchRulePdfUrl,
  getComplianceResult,
  getItemThumbnail,
  startComplianceCheck,
} from '../services/api';
import { IMG } from '../theme/assets';
import type { ComplianceDroppedConcern, ComplianceFinding, ComplianceResult } from '../types';
import { catIconSrc, catOf, icon, SEAL } from './complianceAssets';

interface Props {
  itemId: string;
  title: string;
  isVideo: boolean;
  videoUrl?: string; // 動画ならプレビューで再生・シーク
  pdfUrl?: string; // 文書ならプレビューでPDF表示・該当ページへ
  onBack: () => void;
  onStatusChange?: (status: string) => void; // 一覧側へ最新ステータスを伝える
}

const IN_PROGRESS = new Set(['pending', 'checking']);
const OK_GREEN = '#16a34a';

// 判定シール・観点の定義は一覧画面と共通（components/complianceAssets.ts）

/** 由来（この指摘が何から出たか） */
const SRC: Record<string, { icon: string; label: string }> = {
  transcript: { icon: 'src-transcript', label: '発話' },
  scene: { icon: 'src-scene', label: '情景' },
  fact: { icon: 'src-fact', label: 'ファクト' },
  document: { icon: 'src-document', label: '本文' },
  whole: { icon: 'src-whole', label: '番組全体' },
};
const srcOf = (s?: string) => (s && SRC[s]) || null;
/** whole だけは対象によって呼び方が変わる（番組全体 / 文書全体） */
const srcLabel = (s?: string, isVideo = true) =>
  s === 'whole' && !isVideo ? '文書全体' : (srcOf(s)?.label ?? '');

/** ボタンに添える線画アイコン（Material Symbols のパス） */
const PATH = {
  play: 'M8 5v14l11-7z',
  expand: 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z',
  book: 'M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm0 13.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5z',
  external:
    'M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z',
  page: 'M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z',
};

function Ico({ d, size = 13 }: { d: string; size?: number }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      aria-hidden
      sx={{ width: size, height: size, fill: 'currentColor', flexShrink: 0 }}
    >
      <path d={d} />
    </Box>
  );
}

const fmt = (sec: number) => {
  const s = Math.floor(sec);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const fmtWhen = (iso?: string) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

type RatingFilter = 'all' | 'bad' | 'caution' | 'unjudged';
type Judge = 'bad' | 'caution' | 'unjudged';

const judgeOf = (f: ComplianceFinding): Judge =>
  f.judge_status === 'unjudged' ? 'unjudged' : f.rating === 'bad' ? 'bad' : 'caution';

/** 一覧・時間軸の並び順（そのコンテンツの頭から） */
const posOf = (f: { locator?: { start_sec?: number; page?: number } }, isVideo: boolean) =>
  isVideo
    ? (f.locator?.start_sec ?? Number.POSITIVE_INFINITY)
    : (f.locator?.page ?? Number.POSITIVE_INFINITY);

const locLabel = (loc: ComplianceFinding['locator'] | undefined, isVideo: boolean) => {
  const l = loc ?? {};
  if (l.start_sec != null) return fmt(l.start_sec);
  if (l.page != null) return `p.${l.page}${l.section ? ` §${l.section}` : ''}`;
  return l.raw ?? (isVideo ? '全体' : '全体');
};

export default function ComplianceTab({
  itemId,
  title,
  isVideo,
  videoUrl,
  pdfUrl,
  onBack,
  onStatusChange,
}: Props) {
  const t = useThemeColors();
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  const [data, setData] = useState<ComplianceResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [pdfPage, setPdfPage] = useState(1);
  const [rf, setRf] = useState<RatingFilter>('all');
  const [selId, setSelId] = useState<string | null>(null);
  const [showDropped, setShowDropped] = useState(false);
  const [zoom, setZoom] = useState(false); // プレビューの拡大モーダル
  const [duration, setDuration] = useState(0);
  // 元動画は moov が末尾にあるものだと最初のフレームが出るまで時間がかかる。
  // その間まっ黒にしないよう、サムネイルを poster に出す
  const [poster, setPoster] = useState<string | undefined>();
  const [sourceView, setSourceView] = useState<{
    key: string;
    page: number | null;
    label: string;
  } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const meta: Record<Judge, { label: string; color: string; tint: string }> = {
    bad: {
      label: '抵触の可能性',
      color: t.verdict.inaccurate.color,
      tint: t.verdict.inaccurate.tint,
    },
    caution: { label: '要確認', color: t.verdict.partial.color, tint: t.verdict.partial.tint },
    // 自動判定できなかったもの（judge_status="unjudged"）は条文付きの△と見分けられるようにする
    unjudged: {
      label: '要目視',
      color: t.verdict.unverifiable.color,
      tint: t.verdict.unverifiable.tint,
    },
  };

  const fetchData = useCallback(async () => {
    const res = await getComplianceResult(itemId);
    if (res.success && res.data) {
      setData(res.data);
      onStatusChangeRef.current?.(res.data.complianceStatus ?? '');
      if (IN_PROGRESS.has(res.data.complianceStatus ?? '')) {
        timer.current = setTimeout(fetchData, 5000);
      }
    }
    setLoading(false);
  }, [itemId]);

  useEffect(() => {
    fetchData();
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [fetchData]);

  useEffect(() => {
    if (!isVideo) return;
    let alive = true;
    getItemThumbnail(itemId).then((url) => {
      if (alive && url) setPoster(url);
    });
    return () => {
      alive = false;
    };
  }, [itemId, isVideo]);

  const seek = useCallback((sec?: number, play = false) => {
    if (sec == null || !videoRef.current) return;
    videoRef.current.currentTime = sec;
    if (play) videoRef.current.play();
  }, []);

  const run = async () => {
    setStarting(true);
    const res = await startComplianceCheck(itemId);
    setStarting(false);
    if (res.success) {
      setData((d) => ({
        ...(d ?? { item_id: itemId }),
        complianceStatus: 'pending',
        findings: [],
      }));
      onStatusChangeRef.current?.('pending');
      timer.current = setTimeout(fetchData, 4000);
    }
  };

  const findings = useMemo(() => data?.findings ?? [], [data]);
  const dropped = useMemo(() => data?.dropped_concerns ?? [], [data]);
  const status = data?.complianceStatus;
  const inProg = IN_PROGRESS.has(status ?? '');
  const notRun = !status || status === 'error';
  const completed = status === 'completed';

  const counts = useMemo(() => {
    const c: Record<Judge, number> = { bad: 0, caution: 0, unjudged: 0 };
    for (const f of findings) c[judgeOf(f)] += 1;
    return c;
  }, [findings]);

  const catCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of findings) {
      const k = f.category || 'その他・一般';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [findings]);

  const srcCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const f of findings) {
      const l = srcLabel(f.source_type, isVideo);
      if (l) m.set(l, (m.get(l) ?? 0) + 1);
    }
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [findings, isVideo]);

  // 並び順は「そのコンテンツの頭から」固定（並び替えUIは持たない）
  const rows = useMemo(() => {
    const v = rf === 'all' ? findings : findings.filter((f) => judgeOf(f) === rf);
    return [...v].sort((a, b) => posOf(a, isVideo) - posOf(b, isVideo));
  }, [findings, rf, isVideo]);

  const sel = useMemo(() => rows.find((f) => f.id === selId) ?? rows[0] ?? null, [rows, selId]);

  // 選択したらプレビューを追従させる（動画=その時刻へ・文書=そのページへ。再生はしない）
  useEffect(() => {
    if (!sel) return;
    if (isVideo) seek(sel.locator?.start_sec);
    else if (sel.locator?.page != null) setPdfPage(sel.locator.page);
  }, [sel, isVideo, seek]);

  const total = useMemo(() => {
    if (!isVideo) {
      return Math.max(1, ...findings.map((f) => f.locator?.page ?? 1));
    }
    if (duration > 0) return duration;
    // 尺が取れていないとき（プレビュー未読込）は最後の指摘が右端に貼り付くので少し余白を足す
    const ends = [
      ...findings.map((f) => f.locator?.end_sec ?? f.locator?.start_sec ?? 0),
      ...(data?.cmRanges ?? []).map((r) => r.end_sec),
    ];
    return Math.max(1, ...ends) * 1.08;
  }, [isVideo, duration, findings, data]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const backBtn = (
    <Box
      component="button"
      onClick={onBack}
      sx={{
        alignSelf: 'flex-start',
        flexShrink: 0,
        fontSize: 12.5,
        fontWeight: 700,
        color: t.muted,
        border: `1px solid ${t.line}`,
        backgroundColor: t.paper,
        borderRadius: 1.5,
        px: 1.75,
        py: 0.8,
        cursor: 'pointer',
        boxShadow: '0 6px 18px rgba(30,30,70,.06)',
        '&:hover': { color: t.teal, borderColor: t.teal },
      }}
    >
      ‹ 一覧に戻る
    </Box>
  );

  const heading = (
    <>
      <Box component="img" src={IMG.owl.clipboard} alt="" sx={{ width: 46, height: 46 }} />
      <Typography sx={{ fontSize: 21, fontWeight: 900, color: t.ink, whiteSpace: 'nowrap' }}>
        放送<span style={{ color: t.teal }}>規程</span>チェック
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          color: t.muted,
          borderLeft: `1px solid ${t.line}`,
          pl: 1.25,
          display: { xs: 'none', lg: 'block' },
        }}
      >
        基準・ハンドブックに照らして点検・根拠条文を提示
      </Typography>
    </>
  );

  const curChip = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box
        component="img"
        src={isVideo ? IMG.item.video : IMG.item.document}
        alt=""
        sx={{ width: 20, height: 20, borderRadius: 0.75, objectFit: 'cover' }}
      />
      <Typography sx={{ fontSize: 12, fontWeight: 800, color: t.ink }} noWrap>
        {title}
      </Typography>
    </Box>
  );

  // ===== 未実行 / チェック中 / エラー =====
  if (!completed) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {backBtn}
        <Box sx={{ ...bandSx(t), display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {heading}
          <Box sx={{ ml: 'auto', minWidth: 0 }}>{curChip}</Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            border: `1px solid ${t.line}`,
            borderRadius: 2.5,
            backgroundColor: t.paper,
            textAlign: 'center',
          }}
        >
          <StatusChip status={status} progress={data?.complianceProgress} />
          <Typography sx={{ color: t.muted, maxWidth: '48ch' }}>
            {inProg
              ? '発話・情景・本文・ファクトを放送規程と照合しています…'
              : status === 'error'
                ? '規程チェックでエラーが発生しました。もう一度お試しください。'
                : '放送基準／放送ハンドブックに照らして点検します。最終確認は人が行ってください。'}
          </Typography>
          {(notRun || inProg) && (
            <Button variant="contained" onClick={run} disabled={inProg || starting}>
              {inProg ? 'チェック中…' : '規程チェックを実行'}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  const judgeBtn = (k: Judge) => {
    const active = rf === k;
    const m = meta[k];
    return (
      <Box
        component="button"
        key={k}
        onClick={() => setRf(active ? 'all' : k)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: active ? m.tint : t.paper,
          border: `${active ? 2 : 1}px solid ${active ? m.color : t.line}`,
          borderRadius: 1.75,
          px: active ? 1.4 : 1.5,
          py: active ? 0.55 : 0.65,
          cursor: 'pointer',
          boxShadow: '0 6px 18px rgba(30,30,70,.06)',
          '&:hover': { borderColor: m.color },
        }}
      >
        <Box component="img" src={SEAL[k]} alt="" sx={{ width: 38, height: 38 }} />
        <Box sx={{ textAlign: 'left' }}>
          <Typography sx={{ fontSize: 25, fontWeight: 900, lineHeight: 1, color: m.color }}>
            {counts[k]}
          </Typography>
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: t.muted }}>{m.label}</Typography>
        </Box>
      </Box>
    );
  };

  const when = fmtWhen(data?.complianceCheckedAt);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25, minHeight: 0 }}>
      <SourceModal sourceView={sourceView} onClose={() => setSourceView(null)} />
      <ZoomModal
        open={zoom}
        onClose={() => setZoom(false)}
        isVideo={isVideo}
        videoUrl={videoUrl}
        pdfUrl={pdfUrl}
        page={pdfPage}
        startSec={sel?.locator?.start_sec}
        t={t}
      />

      {backBtn}

      {/* 見出し帯（1行。フィルタの右が空くので内訳もそこに寄せて縦を詰める） */}
      <Box sx={{ ...bandSx(t), flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          {heading}
          {/* フィルタ＋内訳をひとまとめに右寄せし、フィルタを上に置く */}
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.9 }}>
              {(['bad', 'caution', 'unjudged'] as Judge[]).map(judgeBtn)}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1.25,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 11.5, color: t.muted, lineHeight: 1.6 }}>
                AI指摘 <b style={{ color: t.ink }}>{data?.concern_count ?? findings.length}</b> →
                根拠条文あり <b style={{ color: t.ink }}>{findings.length}</b> ／ 該当なし{' '}
                <b style={{ color: t.ink }}>{dropped.length}</b>
              </Typography>
              {srcCounts.length > 0 && (
                <Typography
                  sx={{
                    fontSize: 11.5,
                    color: t.muted,
                    borderLeft: `1px solid ${t.line}`,
                    pl: 1.25,
                    lineHeight: 1.6,
                  }}
                >
                  {srcCounts.map(([l, n]) => `${l} ${n}`).join(' ・ ')}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ ml: 2.5, textAlign: 'right', minWidth: 0 }}>
            {curChip}
            {when && (
              <Typography sx={{ fontSize: 10.5, color: t.faint, mt: 0.25 }}>
                {when} に実行
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* 本体：一覧 / 詳細 / プレビュー の3列 */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: {
            xs: '1fr',
            // --ui-scale=1 にしたぶん実効幅が減るので、1200〜1536px では左右を控えめにする
            md: '360px 1fr',
            lg: '430px 1fr 310px',
            xl: '540px 1fr 390px',
          },
        }}
      >
        {/* 一覧 */}
        <Box sx={{ ...tileSx(t), p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {rows.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center', color: t.faint, fontSize: 13 }}>
                {findings.length === 0
                  ? '抵触のおそれは検出されませんでした。'
                  : '該当する判定の指摘はありません。'}
              </Box>
            ) : (
              <FindingTable
                rows={rows}
                selId={sel?.id ?? null}
                onSelect={setSelId}
                isVideo={isVideo}
                t={t}
                meta={meta}
                catCounts={catCounts}
              />
            )}
          </Box>
          {dropped.length > 0 && (
            <DroppedFooter
              dropped={dropped}
              open={showDropped}
              onToggle={() => setShowDropped((v) => !v)}
              isVideo={isVideo}
              t={t}
            />
          )}
        </Box>

        {/* 詳細 */}
        <Box sx={{ ...tileSx(t), overflowY: 'auto', minHeight: 0 }}>
          {sel ? (
            <Detail
              f={sel}
              meta={meta[judgeOf(sel)]}
              t={t}
              isVideo={isVideo}
              hasVideo={!!videoUrl}
              hasPdf={!!pdfUrl}
              onPlay={() => seek(sel.locator?.start_sec, true)}
              onPage={() => sel.locator?.page != null && setPdfPage(sel.locator.page)}
              onShowSource={() =>
                sel.source_key &&
                setSourceView({
                  key: sel.source_key,
                  page: sel.source_page ?? null,
                  label: sel.source_label ?? '出典',
                })
              }
            />
          ) : (
            <Typography sx={{ fontSize: 12.5, color: t.faint }}>
              一覧から指摘を選ぶと、ここに条文と理由が出ます。
            </Typography>
          )}
        </Box>

        {/* プレビュー・時間軸・観点内訳 */}
        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            flexDirection: 'column',
            gap: 1.25,
            minHeight: 0,
          }}
        >
          <Box sx={{ ...tileSx(t), p: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
              <Label t={t}>{isVideo ? 'この指摘の場面' : 'ドキュメント'}</Label>
              <Box sx={{ flex: 1 }} />
              <Box
                component="button"
                onClick={() => setZoom(true)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.4,
                  fontSize: 10.5,
                  fontWeight: 700,
                  color: t.muted,
                  border: `1px solid ${t.line}`,
                  backgroundColor: 'transparent',
                  borderRadius: 1,
                  px: 0.9,
                  py: 0.3,
                  cursor: 'pointer',
                  '&:hover': { color: t.teal, borderColor: t.teal },
                }}
              >
                <Ico d={PATH.expand} size={12} />
                拡大
              </Box>
            </Box>
            {isVideo ? (
              videoUrl ? (
                <Box
                  component="video"
                  ref={videoRef}
                  src={videoUrl}
                  poster={poster}
                  preload="metadata"
                  controls
                  onLoadedMetadata={(e: React.SyntheticEvent<HTMLVideoElement>) =>
                    setDuration(e.currentTarget.duration || 0)
                  }
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 9',
                    objectFit: 'contain',
                    borderRadius: 1.5,
                    border: `1px solid ${t.line}`,
                    background: '#000',
                    display: 'block',
                  }}
                />
              ) : (
                <PreviewFallback t={t} />
              )
            ) : pdfUrl ? (
              <Box
                component="iframe"
                key={pdfPage}
                title="document-preview"
                src={`${pdfUrl}#page=${pdfPage}`}
                sx={{
                  width: '100%',
                  height: 240,
                  borderRadius: 1.5,
                  border: `1px solid ${t.line}`,
                  background: '#fff',
                }}
              />
            ) : (
              <PreviewFallback t={t} />
            )}
          </Box>

          <Box sx={{ ...tileSx(t), p: 1.25 }}>
            <Label t={t}>{isVideo ? '番組のどこにあるか' : 'どのページにあるか'}</Label>
            <Axis
              rows={findings}
              cmRanges={isVideo ? (data?.cmRanges ?? []) : []}
              total={total}
              isVideo={isVideo}
              selId={sel?.id ?? null}
              onSelect={setSelId}
              t={t}
              meta={meta}
            />
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
              {(['bad', 'caution', 'unjudged'] as Judge[])
                .filter((k) => counts[k] > 0)
                .map((k) => (
                  <Typography key={k} sx={{ fontSize: 10.5, color: t.muted }}>
                    <span style={{ color: meta[k].color, fontWeight: 900 }}>●</span> {meta[k].label}{' '}
                    {counts[k]}
                  </Typography>
                ))}
              {isVideo && (data?.cmRanges?.length ?? 0) > 0 && (
                <Typography sx={{ fontSize: 10.5, color: t.faint }}>
                  斜線 = CM（{data?.cmRanges?.length}帯 {fmt(data?.cmTotalSec ?? 0)}・対象外）
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ ...tileSx(t), p: 1.25, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Label t={t}>どんな観点の指摘か</Label>
            {catCounts.length === 0 ? (
              <Typography sx={{ fontSize: 12, color: t.faint }}>—</Typography>
            ) : (
              catCounts.map(([cat, n]) => (
                <Box key={cat} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.35 }}>
                  <Box
                    component="img"
                    src={catIconSrc(cat)}
                    alt=""
                    sx={{ width: 18, height: 18, objectFit: 'contain', flexShrink: 0 }}
                  />
                  <Typography sx={{ fontSize: 11.5, color: t.ink, flex: 1 }} noWrap title={cat}>
                    {catOf(cat).short}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: t.muted }}>
                    {n}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ================= 部品 =================

const bandSx = (t: ReturnType<typeof useThemeColors>) =>
  ({
    background: `linear-gradient(120deg, ${t.heroFrom}, ${t.heroTo})`,
    border: `1px solid ${t.line}`,
    borderRadius: 2,
    px: 1.75,
    py: 1.1,
  }) as const;

const tileSx = (t: ReturnType<typeof useThemeColors>) =>
  ({
    backgroundColor: t.paper,
    border: `1px solid ${t.line}`,
    borderRadius: 2,
    boxShadow: '0 6px 20px rgba(30,30,70,.06)',
    p: 1.75,
  }) as const;

function Label({
  t,
  children,
}: {
  t: ReturnType<typeof useThemeColors>;
  children: React.ReactNode;
}) {
  return (
    <Typography sx={{ fontSize: 10, fontWeight: 800, color: t.faint, letterSpacing: '.06em' }}>
      {children}
    </Typography>
  );
}

function CatCell({
  category,
  count,
  t,
  iconOnly,
}: {
  category?: string;
  count: number;
  t: ReturnType<typeof useThemeColors>;
  iconOnly?: boolean;
}) {
  const { themeMode } = useThemeMode();
  const c = catOf(category);
  // 吹き出しは不透明でないと下の文字が透ける。トークンの paper はダークだと半透明のため別に持つ
  const tipBg = themeMode === 'dark' ? '#242a3b' : '#ffffff';
  return (
    <Tooltip
      arrow
      placement="left"
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: tipBg,
            color: t.ink,
            border: `1px solid ${t.line}`,
            boxShadow: '0 12px 32px rgba(20,24,40,.22)',
            maxWidth: 280,
            px: 1.5,
            py: 1,
          },
        },
        arrow: { sx: { color: tipBg, '&::before': { border: `1px solid ${t.line}` } } },
      }}
      title={
        <Box sx={{ py: 0.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
            {category ?? 'その他・一般'}
          </Typography>
          <Typography sx={{ fontSize: 11, mt: 0.4, lineHeight: 1.6 }}>{c.desc}</Typography>
          <Typography sx={{ fontSize: 10.5, mt: 0.4, opacity: 0.75 }}>
            このコンテンツでは {count}件
          </Typography>
        </Box>
      }
    >
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
        <Box
          component="img"
          src={catIconSrc(category)}
          alt=""
          sx={{ width: 17, height: 17, objectFit: 'contain', flexShrink: 0 }}
        />
        {!iconOnly && (
          <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 700 }} noWrap>
            {c.short}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

function SrcCell({
  type,
  t,
  isVideo = true,
  iconOnly,
}: {
  type?: string;
  t: ReturnType<typeof useThemeColors>;
  isVideo?: boolean;
  iconOnly?: boolean;
}) {
  const s = srcOf(type);
  if (!s) return <Typography sx={{ fontSize: 11, color: t.faint }}>—</Typography>;
  return (
    <Box
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.55 }}
      title={iconOnly ? srcLabel(type, isVideo) : undefined}
    >
      <Box
        component="img"
        src={icon(s.icon)}
        alt=""
        sx={{ width: 16, height: 16, objectFit: 'contain', flexShrink: 0 }}
      />
      {!iconOnly && (
        <Typography sx={{ fontSize: 11, color: t.muted, fontWeight: 700 }} noWrap>
          {srcLabel(type, isVideo)}
        </Typography>
      )}
    </Box>
  );
}

function FindingTable({
  rows,
  selId,
  onSelect,
  isVideo,
  t,
  meta,
  catCounts,
}: {
  rows: ComplianceFinding[];
  selId: string | null;
  onSelect: (id: string) => void;
  isVideo: boolean;
  t: ReturnType<typeof useThemeColors>;
  meta: Record<Judge, { label: string; color: string; tint: string }>;
  catCounts: [string, number][];
}) {
  const countOf = (c?: string) => catCounts.find(([k]) => k === (c || 'その他・一般'))?.[1] ?? 0;
  const th = {
    fontSize: 10,
    fontWeight: 800,
    color: t.faint,
    letterSpacing: '.06em',
    textAlign: 'left' as const,
    padding: '8px 10px',
    position: 'sticky' as const,
    top: 0,
    backgroundColor: t.paper,
    borderBottom: `1px solid ${t.line}`,
    zIndex: 1,
  };
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <Box component="thead">
        <Box component="tr">
          <Box component="th" sx={{ ...th, width: 38 }} />
          <Box component="th" sx={{ ...th, width: 50, padding: '8px 4px' }}>
            {isVideo ? '時刻' : 'ページ'}
          </Box>
          <Box component="th" sx={{ ...th, width: 30, padding: '8px 4px' }}>
            由来
          </Box>
          <Box component="th" sx={{ ...th, padding: '8px 8px' }}>
            指摘
          </Box>
          <Box component="th" sx={{ ...th, width: 104, padding: '8px 8px' }}>
            観点
          </Box>
        </Box>
      </Box>
      <Box component="tbody">
        {rows.map((f) => {
          const k = judgeOf(f);
          const on = f.id === selId;
          const td = {
            padding: '9px 6px',
            borderBottom: `1px solid ${t.line}`,
            verticalAlign: 'top' as const,
          };
          return (
            <Box
              component="tr"
              key={f.id}
              onClick={() => onSelect(f.id)}
              sx={{
                cursor: 'pointer',
                backgroundColor: on ? meta[k].tint : 'transparent',
                boxShadow: on ? `inset 3px 0 0 ${meta[k].color}` : 'none',
                '&:hover': { backgroundColor: on ? meta[k].tint : t.heroFrom },
              }}
            >
              <Box component="td" sx={td}>
                <Box
                  component="img"
                  src={SEAL[k]}
                  alt={meta[k].label}
                  title={meta[k].label}
                  sx={{ width: 22, height: 22, objectFit: 'contain', display: 'block' }}
                />
              </Box>
              <Box
                component="td"
                sx={{
                  ...td,
                  fontSize: 11,
                  fontWeight: 800,
                  color: t.muted,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {locLabel(f.locator, isVideo)}
              </Box>
              <Box component="td" sx={td}>
                <SrcCell type={f.source_type} t={t} isVideo={isVideo} iconOnly />
              </Box>
              <Box
                component="td"
                sx={{
                  ...td,
                  padding: '9px 8px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: t.ink,
                  lineHeight: 1.55,
                }}
              >
                {f.expression}
              </Box>
              <Box component="td" sx={td}>
                <CatCell category={f.category} count={countOf(f.category)} t={t} />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function DroppedFooter({
  dropped,
  open,
  onToggle,
  isVideo,
  t,
}: {
  dropped: ComplianceDroppedConcern[];
  open: boolean;
  onToggle: () => void;
  isVideo: boolean;
  t: ReturnType<typeof useThemeColors>;
}) {
  const sorted = [...dropped].sort((a, b) => posOf(a, isVideo) - posOf(b, isVideo));
  return (
    <Box sx={{ borderTop: `1px solid ${t.line}`, flexShrink: 0 }}>
      <Box
        component="button"
        onClick={onToggle}
        sx={{
          width: '100%',
          display: 'block',
          px: 1.5,
          py: 1,
          border: 'none',
          backgroundColor: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          '&:focus': { outline: 'none' },
          '&:focus-visible': { outline: `2px solid ${t.teal}`, outlineOffset: '-2px' },
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: t.muted }}>
          根拠条文が見つからなかった {dropped.length}件 {open ? '▾' : '▸'}
        </Typography>
        <Typography sx={{ fontSize: 10, color: t.faint, lineHeight: 1.5 }}>
          AI が指摘したが、規程集に当たる条文が無かったもの
        </Typography>
      </Box>
      {open && (
        <Box sx={{ maxHeight: 180, overflowY: 'auto', px: 1.5, pb: 1.25 }}>
          {sorted.map((d) => (
            <Box key={d.id} sx={{ py: 0.6, borderTop: `1px solid ${t.line}` }}>
              <Typography sx={{ fontSize: 11.5, color: t.muted, lineHeight: 1.5 }}>
                <span
                  style={{ color: t.faint, fontVariantNumeric: 'tabular-nums', marginRight: 6 }}
                >
                  {locLabel(d.locator, isVideo)}
                </span>
                {d.expression}
              </Typography>
              {d.scan_reason && (
                <Typography sx={{ fontSize: 10.5, color: t.faint, lineHeight: 1.5, mt: 0.25 }}>
                  {d.scan_reason}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function Detail({
  f,
  meta,
  t,
  isVideo,
  hasVideo,
  hasPdf,
  onPlay,
  onPage,
  onShowSource,
}: {
  f: ComplianceFinding;
  meta: { label: string; color: string; tint: string };
  t: ReturnType<typeof useThemeColors>;
  isVideo: boolean;
  hasVideo: boolean;
  hasPdf: boolean;
  onPlay: () => void;
  onPage: () => void;
  onShowSource: () => void;
}) {
  const k = judgeOf(f);
  const ruleHead = [f.rule_chapter, f.rule_title].filter(Boolean).join(' ');
  const btnSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    fontSize: 11,
    fontWeight: 700,
    color: t.muted,
    border: `1px solid ${t.line}`,
    backgroundColor: 'transparent',
    borderRadius: 1,
    px: 1.1,
    py: 0.5,
    cursor: 'pointer',
    '&:hover': { color: t.teal, borderColor: t.teal },
  } as const;

  return (
    <Box sx={{ maxWidth: 980 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box component="img" src={SEAL[k]} alt="" sx={{ width: 38, height: 38 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: meta.color }}>
            {meta.label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Box
              component="img"
              src={catIconSrc(f.category)}
              alt=""
              sx={{ width: 15, height: 15, objectFit: 'contain' }}
            />
            <Typography
              sx={{ fontSize: 10.5, color: t.muted }}
              noWrap
              title={f.category ?? 'その他・一般'}
            >
              {catOf(f.category).short}
            </Typography>
            <Box sx={{ width: '1px', height: 11, backgroundColor: t.line }} />
            <SrcCell type={f.source_type} t={t} isVideo={isVideo} />
            <Box sx={{ width: '1px', height: 11, backgroundColor: t.line }} />
            <Typography sx={{ fontSize: 10.5, color: t.muted, fontVariantNumeric: 'tabular-nums' }}>
              {locLabel(f.locator, isVideo)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 15, fontWeight: 800, color: t.ink, lineHeight: 1.65, my: 1.25 }}>
        {f.expression}
      </Typography>

      {k === 'unjudged' && (
        <Box
          sx={{
            px: 1.25,
            py: 0.9,
            borderRadius: 1,
            backgroundColor: meta.tint,
            border: `1px solid ${meta.color}55`,
            mb: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: meta.color }}>
            自動判定できませんでした（該当条文を特定できず）
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: t.muted, mt: 0.25 }}>
            下の内容を手がかりに、担当者の目視で判断してください。
          </Typography>
        </Box>
      )}

      {f.rule_excerpt && (
        <Box
          sx={{
            backgroundColor: t.heroTo,
            border: `1px solid ${t.line}`,
            borderLeft: `3px solid ${t.teal}`,
            borderRadius: 1,
            px: 1.25,
            py: 1,
            mb: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: t.teal, mb: 0.5 }}>
            {ruleHead || '該当条文'}
            {f.rule_id ? ` ／ ${f.rule_id}` : ''}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: t.muted, lineHeight: 1.8 }}>
            {f.rule_excerpt}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: '72px 1fr', gap: '10px 14px' }}>
        {f.explanation && <Row t={t} label="なぜ" value={f.explanation} />}
        {f.suggestion && <Row t={t} label="どう直す" value={f.suggestion} />}
        {f.scan_reason && <Row t={t} label="AI指摘の理由" value={f.scan_reason} muted />}
      </Box>

      <Box sx={{ display: 'flex', gap: 0.9, mt: 1.5, flexWrap: 'wrap' }}>
        {isVideo && hasVideo && f.locator?.start_sec != null && (
          <Box
            component="button"
            onClick={onPlay}
            sx={{ ...btnSx, color: t.teal, borderColor: t.teal }}
          >
            <Ico d={PATH.play} size={12} />
            {locLabel(f.locator, isVideo)} から再生
          </Box>
        )}
        {!isVideo && hasPdf && f.locator?.page != null && (
          <Box
            component="button"
            onClick={onPage}
            sx={{ ...btnSx, color: t.teal, borderColor: t.teal }}
          >
            <Ico d={PATH.page} />
            {locLabel(f.locator, isVideo)} を表示
          </Box>
        )}
        {f.source_key && (
          <Box component="button" onClick={onShowSource} sx={btnSx}>
            <Ico d={PATH.book} />
            出典を見る
          </Box>
        )}
        {f.source_url && (
          <Link
            href={f.source_url}
            target="_blank"
            rel="noopener"
            sx={{ ...btnSx, textDecoration: 'none' }}
          >
            <Ico d={PATH.external} />
            規程を開く
          </Link>
        )}
      </Box>
      {f.source_label && (
        <Typography sx={{ fontSize: 10, color: t.faint, mt: 0.75 }}>
          出典: {f.source_label}
          {f.source_page != null ? `（p.${f.source_page}）` : ''}
        </Typography>
      )}
    </Box>
  );
}

function Row({
  t,
  label,
  value,
  muted,
}: {
  t: ReturnType<typeof useThemeColors>;
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <>
      <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: t.faint, pt: 0.35 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: muted ? t.muted : t.ink, lineHeight: 1.8 }}>
        {value}
      </Typography>
    </>
  );
}

function Axis({
  rows,
  cmRanges,
  total,
  isVideo,
  selId,
  onSelect,
  t,
  meta,
}: {
  rows: ComplianceFinding[];
  cmRanges: { start_sec: number; end_sec: number; label?: string }[];
  total: number;
  isVideo: boolean;
  selId: string | null;
  onSelect: (id: string) => void;
  t: ReturnType<typeof useThemeColors>;
  meta: Record<Judge, { label: string; color: string; tint: string }>;
}) {
  // 動画は時刻そのまま、文書は「ページの中央」に置く（1/1 のとき左端に貼り付かないように）
  const pct = (v: number) =>
    `${Math.min(100, Math.max(0, ((isVideo ? v : v - 0.5) / total) * 100))}%`;
  return (
    <Box
      sx={{
        position: 'relative',
        height: 26,
        mt: 0.75,
        borderRadius: 1,
        backgroundColor: t.heroFrom,
        border: `1px solid ${t.line}`,
        overflow: 'hidden',
      }}
    >
      {cmRanges.map((r) => (
        <Box
          key={`cm-${r.start_sec}`}
          title={`CM ${fmt(r.start_sec)}–${fmt(r.end_sec)}${r.label ? ` ${r.label}` : ''}`}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${(r.start_sec / total) * 100}%`,
            width: `${((r.end_sec - r.start_sec) / total) * 100}%`,
            backgroundImage: `repeating-linear-gradient(45deg, ${t.line} 0 3px, transparent 3px 6px)`,
          }}
        />
      ))}
      {rows.map((f) => {
        const v = posOf(f, isVideo);
        if (!Number.isFinite(v)) return null;
        const k = judgeOf(f);
        return (
          <Box
            component="button"
            key={f.id}
            onClick={() => onSelect(f.id)}
            title={`${locLabel(f.locator, isVideo)}　${f.expression.slice(0, 40)}`}
            sx={{
              position: 'absolute',
              top: 3,
              bottom: 3,
              left: pct(v),
              width: f.id === selId ? '4px' : '2px',
              marginLeft: '-1px',
              padding: 0,
              border: 'none',
              borderRadius: '2px',
              backgroundColor: meta[k].color,
              opacity: f.id === selId ? 1 : 0.7,
              cursor: 'pointer',
            }}
          />
        );
      })}
    </Box>
  );
}

function PreviewFallback({ t }: { t: ReturnType<typeof useThemeColors> }) {
  return (
    <Box
      sx={{
        width: '100%',
        aspectRatio: '16 / 9',
        borderRadius: 1.5,
        border: `1px dashed ${t.line}`,
        display: 'grid',
        placeItems: 'center',
        color: t.faint,
        fontSize: 12,
      }}
    >
      プレビューを読み込めませんでした
    </Box>
  );
}

function ZoomModal({
  open,
  onClose,
  isVideo,
  videoUrl,
  pdfUrl,
  page,
  startSec,
  t,
}: {
  open: boolean;
  onClose: () => void;
  isVideo: boolean;
  videoUrl?: string;
  pdfUrl?: string;
  page: number;
  startSec?: number;
  t: ReturnType<typeof useThemeColors>;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <Box sx={{ p: 1.5 }}>
        <WideDialogHeader
          img={IMG.compliance.preview}
          title={isVideo ? 'プレビュー' : `ドキュメント（p.${page}）`}
          subtitle={
            isVideo
              ? startSec != null
                ? `指摘のあった ${fmt(startSec)} から再生します`
                : 'もとの映像を見ます'
              : 'もとの資料の該当ページを見ます'
          }
          onClose={onClose}
        />
        {isVideo ? (
          videoUrl ? (
            <video
              src={startSec != null ? `${videoUrl}#t=${Math.floor(startSec)}` : videoUrl}
              controls
              autoPlay
              style={{ width: '100%', maxHeight: '76vh', background: '#000', borderRadius: 8 }}
            />
          ) : (
            <PreviewFallback t={t} />
          )
        ) : pdfUrl ? (
          <iframe
            title="document-zoom"
            src={`${pdfUrl}#page=${page}`}
            style={{ width: '100%', height: '76vh', border: 'none', background: '#fff' }}
          />
        ) : (
          <PreviewFallback t={t} />
        )}
      </Box>
    </Dialog>
  );
}

/**
 * 大きく開く窓の見出し。
 *
 * 共通モーダル(AppDialog)に寄せると中身の幅が狭くなってしまうので、ここでは
 * 見出しの作り（絵＋題名＋下の太線＋右上の×）だけを合わせている。
 */
function WideDialogHeader({
  img,
  title,
  subtitle,
  onClose,
  extra,
}: {
  img: string;
  title: string;
  subtitle?: string;
  onClose: () => void;
  extra?: React.ReactNode;
}) {
  const t = useThemeColors();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1,
        pb: 1.25,
        mb: 1.25,
        borderBottom: `3px solid ${t.teal}`,
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '12px',
          background: `${t.teal}1f`,
        }}
      >
        <Box
          component="img"
          src={img}
          alt=""
          sx={{ width: 32, height: 32, objectFit: 'contain' }}
        />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 900, color: t.ink, lineHeight: 1.4 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 11, color: t.muted, mt: 0.3, lineHeight: 1.6 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {extra}
      <IconButton
        onClick={onClose}
        size="small"
        disableRipple
        sx={{
          color: t.faint,
          alignSelf: 'flex-start',
          '&:hover': { color: t.ink, background: 'transparent' },
        }}
      >
        <CloseIcon sx={{ fontSize: 19 }} />
      </IconButton>
    </Box>
  );
}

function SourceModal({
  sourceView,
  onClose,
}: {
  sourceView: { key: string; page: number | null; label: string } | null;
  onClose: () => void;
}) {
  const t = useThemeColors();
  // 規程PDFはログインした人だけが読めるので、素のURLではなく中身を取ってきて開く。
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const sourceKey = sourceView?.key ?? null;

  useEffect(() => {
    if (!sourceKey) {
      setPdfObjectUrl(null);
      setPdfError(null);
      return;
    }
    let objectUrl: string | null = null;
    let alive = true;
    setPdfObjectUrl(null);
    setPdfError(null);
    fetchRulePdfUrl(sourceKey)
      .then((url) => {
        objectUrl = url;
        if (alive) {
          setPdfObjectUrl(url);
        } else {
          URL.revokeObjectURL(url);
        }
      })
      .catch((err) => {
        console.error('規程PDFの取得に失敗:', err);
        if (alive) setPdfError('規程PDFを開けませんでした。ログインし直してからお試しください。');
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sourceKey]);

  // 放送ハンドブックはページ番号を持たない（規程集側に無い）ので、その場合は先頭から開く。
  const src =
    pdfObjectUrl && sourceView
      ? `${pdfObjectUrl}${sourceView.page != null ? `#page=${sourceView.page}` : ''}`
      : '';
  return (
    <Dialog open={!!sourceView} onClose={onClose} maxWidth="lg" fullWidth>
      {sourceView && (
        <Box sx={{ p: 1.5 }}>
          <WideDialogHeader
            img={IMG.preview.document}
            title={`出典: ${sourceView.label}`}
            subtitle={
              sourceView.page != null
                ? `${sourceView.page} ページ目を開いています`
                : '先頭から開いています'
            }
            onClose={onClose}
            extra={
              <Button
                size="small"
                href={src}
                target="_blank"
                rel="noopener"
                disabled={!src}
                sx={{ flexShrink: 0 }}
              >
                別タブで開く
              </Button>
            }
          />
          {pdfError ? (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '76vh' }}>
              <Typography sx={{ fontSize: 13, color: t.muted }}>{pdfError}</Typography>
            </Box>
          ) : src ? (
            <Box
              component="iframe"
              key={src}
              title="source-pdf"
              src={src}
              sx={{ width: '100%', height: '76vh', border: 'none', background: '#fff' }}
            />
          ) : (
            <Box sx={{ display: 'grid', placeItems: 'center', height: '76vh' }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
      )}
    </Dialog>
  );
}

function StatusChip({ status, progress }: { status?: string; progress?: number }) {
  const map: Record<string, { label: string; color: string }> = {
    completed: { label: '規程チェック 完了', color: OK_GREEN },
    error: { label: 'エラー', color: '#ef4444' },
  };
  const inProg = IN_PROGRESS.has(status ?? '');
  const m = map[status ?? ''] ?? { label: '未実行', color: '#64748b' };
  return (
    <Chip
      icon={inProg ? <CircularProgress size={14} sx={{ color: '#fff !important' }} /> : undefined}
      label={inProg ? `チェック中… (${progress ?? 0}%)` : m.label}
      sx={{ backgroundColor: inProg ? '#3b82f6' : m.color, color: '#fff', fontWeight: 600 }}
    />
  );
}
